import { QueryClient } from '@tanstack/react-query'
import {
  SSEEvent,
  SSEEventType,
  SSEEventHandler,
  SSEEventDataMap,
  QueryKey,
  QueryInvalidationConfig
} from '@/types/sse'

class SSEEventManager {
  private eventHandlers = new Map<SSEEventType, Set<SSEEventHandler>>()
  private queryClient: QueryClient | null = null
  private invalidationConfigs: QueryInvalidationConfig[] = []

  constructor() {
    this.setupDefaultInvalidationConfigs()
  }

  // Set the React Query client instance
  setQueryClient(queryClient: QueryClient) {
    this.queryClient = queryClient
  }

  // Subscribe to an event type
  subscribe<T extends SSEEventType>(
    eventType: T,
    handler: SSEEventHandler<SSEEventDataMap[T]>
  ): () => void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set())
    }

    const handlers = this.eventHandlers.get(eventType)!
    handlers.add(handler as SSEEventHandler)

    // Return unsubscribe function
    return () => {
      handlers.delete(handler as SSEEventHandler)
      if (handlers.size === 0) {
        this.eventHandlers.delete(eventType)
      }
    }
  }

  // Publish an event to all subscribers
  publish(eventData: SSEEvent) {
    console.log('SSE Event published:', eventData)

    // Notify event handlers
    const handlers = this.eventHandlers.get(eventData.event)
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(eventData)
        } catch (error) {
          console.error(`Error in SSE event handler for ${eventData.event}:`, error)
        }
      })
    }

    // Auto-invalidate queries
    this.invalidateQueriesForEvent(eventData)
  }

  // Invalidate queries based on event
  private invalidateQueriesForEvent(eventData: SSEEvent) {
    if (!this.queryClient) return

    const relevantConfigs = this.invalidationConfigs.filter(
      config => config.event === eventData.event
    )

    for (const config of relevantConfigs) {
      for (const queryKeyPattern of config.queryKeyPatterns) {
        // Check if we should invalidate based on custom logic
        if (config.shouldInvalidate && !config.shouldInvalidate(eventData, queryKeyPattern)) {
          continue
        }

        try {
          // Generate specific query key based on event data
          const specificQueryKey = this.generateSpecificQueryKey(eventData, queryKeyPattern)

          console.log(`Invalidating query for event ${eventData.event}:`, specificQueryKey)

          if (specificQueryKey) {
            this.queryClient.invalidateQueries({
              queryKey: specificQueryKey,
              exact: false // Allow partial matching
            })
          } else {
            // Fallback to pattern-based invalidation
            this.queryClient.invalidateQueries({
              queryKey: queryKeyPattern,
              exact: false
            })
          }
        } catch (error) {
          console.error(`Error invalidating query for ${eventData.event}:`, error)
        }
      }
    }
  }

  // Generate specific query key based on event data
  private generateSpecificQueryKey(eventData: SSEEvent, pattern: QueryKey): QueryKey | null {
    try {
      // Replace placeholders in query key pattern with actual values from event data
      const specificKey = pattern.map(segment => {
        if (typeof segment === 'string') {
          // Handle room-related events
          if ('roomId' in eventData && segment === ':roomId') {
            return eventData.roomId
          }
          // Handle round-related events
          if ('roundId' in eventData && segment === ':roundId') {
            return eventData.roundId
          }
          // Handle specific round data
          if (eventData.event === 'roundUpdate' || eventData.event === 'roundFinish') {
            if (segment === ':roundId' && 'roundData' in eventData) {
              return eventData.roundData.id
            }
          }
          // Handle amount change events
          if (eventData.event === 'amountChange' && segment === ':code') {
            return eventData.code
          }
        }
        return segment
      })

      return specificKey
    } catch (error) {
      console.error('Error generating specific query key:', error)
      return null
    }
  }

  // Add custom invalidation configuration
  addInvalidationConfig(config: QueryInvalidationConfig) {
    this.invalidationConfigs.push(config)
  }

  // Set up default query invalidation configurations
  private setupDefaultInvalidationConfigs() {
    this.invalidationConfigs = [
      // Round update events
      {
        event: 'roundUpdate',
        queryKeyPatterns: [
          ['room', ':roomId', 'current-round'],
          ['room', ':roomId', 'participation'],
          ['round', ':roundId', 'seats']
        ],
        shouldInvalidate: (eventData, queryKey) => {
          if (eventData.event === 'roundUpdate') {
            // Invalidate room queries for the specific room
            if (queryKey[0] === 'room' && queryKey[1] === ':roomId') {
              return true
            }
            // Invalidate round queries for the specific round
            if (queryKey[0] === 'round' && 'roundData' in eventData) {
              return true
            }
          }
          return false
        }
      },

      // Round finish events
      {
        event: 'roundFinish',
        queryKeyPatterns: [
          ['room', ':roomId', 'current-round'],
          ['room', ':roomId', 'participation'],
          ['round', ':roundId', 'seats'],
          ['rooms', 'translations'] // Update home page
        ]
      },

      // Seats refresh events
      {
        event: 'seatsRefresh',
        queryKeyPatterns: [
          ['round', ':roundId', 'seats']
        ]
      },

      // Seat update events (single seat changes)
      {
        event: 'seatUpdate',
        queryKeyPatterns: [
          ['round', ':roundId', 'seats']
        ]
      },

      // Amount change events
      {
        event: 'amountChange',
        queryKeyPatterns: [
          ['user', 'balance'],
          ['user', 'balance', ':code']
        ],
        shouldInvalidate: (eventData, queryKey) => {
          if (eventData.event === 'amountChange') {
            // Always invalidate general balance queries
            if (queryKey[0] === 'user' && queryKey[1] === 'balance' && queryKey.length === 2) {
              return true
            }
            // Invalidate specific currency balance if it matches
            if (queryKey[0] === 'user' && queryKey[1] === 'balance' && queryKey[2] === ':code') {
              return true
            }
          }
          return false
        }
      }
    ]
  }

  // Get all active subscriptions (for debugging)
  getActiveSubscriptions(): Record<SSEEventType, number> {
    const counts: Record<string, number> = {}
    this.eventHandlers.forEach((handlers, eventType) => {
      counts[eventType] = handlers.size
    })
    return counts as Record<SSEEventType, number>
  }

  // Clear all subscriptions
  clear() {
    this.eventHandlers.clear()
  }
}

// Export singleton instance
export const sseEventManager = new SSEEventManager()